import {
  billboarding,
  color,
  cos,
  deltaTime,
  float,
  Fn,
  hash,
  If,
  instancedArray,
  instanceIndex,
  length,
  positionGeometry,
  positionLocal,
  sin,
  smoothstep,
  texture,
  time,
  uint,
  uniform,
  uv,
  vec2,
  vec3,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

export class Rain {
  camera!: THREE.PerspectiveCamera
  renderer!: THREE.WebGPURenderer
  container!: THREE.Object3D
  debugFolder?: FolderApi

  computeParticles!: THREE.ComputeNode

  maxParticleCount: number
  instanceCount: number

  positionBuffer: THREE.StorageBufferNode
  velocityBuffer: THREE.StorageBufferNode
  ripplePositionBuffer: THREE.StorageBufferNode
  rippleTimeBuffer: THREE.StorageBufferNode
  tiltAngleBuffer: THREE.StorageBufferNode

  renderOrder = 0

  settings = {
    dropHeight: 0.3,
    dropWidth: 0.006,
    dropColor: uniform(color("#384352")),
    intensity: uniform(0.66),
    velocity: uniform(0.9), // Good value: 0.6
    ripplesOpacity: uniform(30),
  }

  constructor({
    container,
    camera,
    renderer,
    maxParticleCount = 2000, // Maximum number of raindrops
    renderOrder = 0,
    debugFolder,
  }: {
    container: THREE.Object3D
    camera: THREE.PerspectiveCamera
    renderer: THREE.WebGPURenderer
    maxParticleCount?: number
    renderOrder?: number
    debugFolder?: FolderApi
  }) {
    this.container = container
    this.camera = camera
    this.renderer = renderer

    this.renderOrder = renderOrder

    this.debugFolder = debugFolder

    this.maxParticleCount = maxParticleCount
    this.instanceCount = maxParticleCount / 2

    // Create GPU buffers for particle data
    // These store the state of each raindrop
    this.positionBuffer = instancedArray(this.maxParticleCount, "vec3") // Current position of each raindrop
    this.velocityBuffer = instancedArray(this.maxParticleCount, "vec3") // Velocity vector of each raindrop
    this.ripplePositionBuffer = instancedArray(this.maxParticleCount, "vec3") // Position where ripples start
    this.rippleTimeBuffer = instancedArray(this.maxParticleCount, "vec3") // Timing data for ripple animation
    this.tiltAngleBuffer = instancedArray(this.maxParticleCount, "float") // Tilt angle for each raindrop (stored in x component)

    this.createMeshes()

    this.setDebug()

    this.init()
  }

  init() {
    // Create a render target for collision detection
    // This stores height data for the ground surface
    const collisionPosRT = new THREE.RenderTarget(16, 16)
    collisionPosRT.texture.type = THREE.HalfFloatType
    collisionPosRT.texture.magFilter = THREE.NearestFilter
    collisionPosRT.texture.minFilter = THREE.NearestFilter
    collisionPosRT.texture.generateMipmaps = false

    // GPU Compute Shader: Initialize particle positions and velocities
    const randUint = () => uint(Math.random() * 0xffffff)

    const computeInit = Fn(() => {
      // Get references to this particle's data
      const position = this.positionBuffer.element(instanceIndex)
      const velocity = this.velocityBuffer.element(instanceIndex)
      const rippleTime = this.rippleTimeBuffer.element(instanceIndex)
      const tiltAngle = this.tiltAngleBuffer.element(instanceIndex)

      // Generate random values for initial positioning
      const randX = hash(instanceIndex)
      const randY = hash(instanceIndex.add(randUint()))
      const randZ = hash(instanceIndex.add(randUint()))

      // Set initial position: spread raindrops across the scene
      position.x = randX.mul(20).add(-10) // X: -10 to 10
      position.y = randY.mul(25) // Y: 0 to 25 (above ground)
      position.z = randZ.mul(15).add(-10)

      // Set initial velocity: downward movement with slight randomness
      velocity.y = randX.mul(-0.9).add(-0.2) // Y velocity: -0.2 to -1.1

      // Initialize random tilt angle (±20 degrees) for natural variation
      // Generate random value in shader using hash function
      const tiltAngleValue = hash(instanceIndex.add(randUint()))
        .mul(0.349) // 20 degrees in radians ≈ 0.349, spread is ±20 so range is 0.698
        .sub(0.175) // Center around 0, range from -0.349 to +0.349
      tiltAngle.x = tiltAngleValue

      // Initialize ripple timing (100 = no active ripple)
      rippleTime.x = 100 as unknown as THREE.Node
    })().compute(this.maxParticleCount)

    // GPU Compute Shader: Update particle physics and handle collisions
    const computeUpdate = Fn(() => {
      // Helper function to convert world position to texture coordinates
      const getCoord = (pos: THREE.Node) => pos.add(50).div(100)

      // Get references to this particle's data
      const position = this.positionBuffer.element(instanceIndex)
      const velocity = this.velocityBuffer.element(instanceIndex)
      const ripplePosition = this.ripplePositionBuffer.element(instanceIndex)
      const rippleTime = this.rippleTimeBuffer.element(instanceIndex)

      // Apply velocity to position (gravity effect)
      position.addAssign(velocity.mul(this.settings.velocity))

      // Update ripple animation time (slower for more visible ripples)
      rippleTime.x = rippleTime.x.add(deltaTime.mul(1.5))

      // Check collision with ground using render target texture
      const collisionArea = texture(
        collisionPosRT.texture,
        getCoord(position.xz),
      )

      const surfaceOffset = 0.01 // Small offset above ground surface

      // Calculate floor height at this position
      const floorPosition = collisionArea.y.add(surfaceOffset)

      // Floor collision detection and raindrop reset
      const ripplePivotOffsetY = -this.settings.dropHeight / 2

      // If raindrop hits the ground
      If(position.y.add(ripplePivotOffsetY).lessThan(floorPosition), () => {
        // Reset raindrop to top of scene
        position.y = 25 as unknown as THREE.Node

        // Set ripple position at impact point
        ripplePosition.xz = position.xz
        ripplePosition.y = floorPosition

        // Start ripple animation (1 = beginning of ripple)
        rippleTime.x = 1 as unknown as THREE.Node

        // Randomize next drop position to avoid same spot
        position.x = hash(instanceIndex.add(time)).mul(20).add(-10)
        position.z = hash(instanceIndex.add(time.add(randUint())))
          .mul(15)
          .add(-10)
      })

      // Check if ripple has reached ground level
      const rippleOnSurface = texture(
        collisionPosRT.texture,
        getCoord(ripplePosition.xz),
      )

      const rippleFloorArea = rippleOnSurface.y.add(surfaceOffset)

      // If ripple is below ground, stop the ripple animation
      If(ripplePosition.y.greaterThan(rippleFloorArea), () => {
        // Large value = no active ripple
        rippleTime.x = 1000 as unknown as THREE.Node
      })
    })

    // Create the compute shader for particle updates
    this.computeParticles = computeUpdate().compute(this.maxParticleCount)

    // Initialize particle positions on GPU
    this.renderer.computeAsync(computeInit)
  }

  createMeshes() {
    // Create raindrop visual material
    const rainMaterial = new THREE.MeshBasicNodeMaterial()
    const uvValue = uv()
    // Create gradient effect for raindrops using UV coordinates
    const gradient = length(vec2(uvValue.x, uvValue.y.sub(0.5)))
      .oneMinus()
      .pow(2)
      .mul(2)
      .mul(this.settings.intensity)

    rainMaterial.colorNode = gradient.mul(this.settings.dropColor)

    rainMaterial.opacityNode = Fn(() => {
      // Get the world position of this particle instance
      const worldPos = this.positionBuffer.toAttribute()

      // Calculate distance to each boundary edge
      const distToLeft = worldPos.x.sub(-10) // Distance from X = -10
      const distToRight = float(10).sub(worldPos.x) // Distance from X = 10
      const distToBack = worldPos.z.sub(-10) // Distance from Z = -10
      const distToFront = float(5).sub(worldPos.z) // Distance from Z = 5

      // Find the minimum distance to any edge (closest boundary)
      const minDist = distToLeft
        .min(distToRight)
        .min(distToBack)
        .min(distToFront)

      // Fade out near edges using smoothstep
      // Adjust the second parameter (e.g., 2.0) to control fade width
      // This will fade from 0 to 1 as distance increases from 0 to 2.0
      return smoothstep(0, 10, minDist)
    })()

    // Apply rotation to local position before billboarding
    const tiltAngle = this.tiltAngleBuffer.toAttribute()
    rainMaterial.positionNode = Fn(() => {
      const localPos = positionLocal

      // Rotate local position around Z-axis using tilt angle
      const cosTilt = cos(tiltAngle)
      const sinTilt = sin(tiltAngle)
      return vec3(
        localPos.x.mul(cosTilt).sub(localPos.y.mul(sinTilt)),
        localPos.x.mul(sinTilt).add(localPos.y.mul(cosTilt)),
        localPos.z,
      )
    })()

    // Make raindrops always face the camera
    rainMaterial.vertexNode = billboarding({
      position: this.positionBuffer.toAttribute(),
    })
    rainMaterial.transparent = true // Enable transparency
    rainMaterial.opacity = 0.2 // Semi-transparent
    rainMaterial.side = THREE.DoubleSide // Render both sides
    rainMaterial.forceSinglePass = false // Optimize rendering
    rainMaterial.depthWrite = false // Don't write to depth buffer
    rainMaterial.depthTest = true // Still test depth

    // Create raindrop geometry (thin vertical planes)
    const rainParticles = new THREE.Mesh(
      new THREE.PlaneGeometry(
        this.settings.dropWidth,
        this.settings.dropHeight,
      ),
      rainMaterial,
    )
    rainParticles.frustumCulled = false
    rainParticles.count = this.instanceCount // Set number of instances
    // rainParticles.renderOrder = this.renderOrder
    this.container.add(rainParticles)

    // Create ripple effect material
    const rippleTime = this.rippleTimeBuffer.element(instanceIndex).x

    // Ripple effect function: creates expanding circular waves
    const rippleEffect = Fn(() => {
      const uvCoord = uv()
      const center = vec2(0.5) // Center of the ripple plane
      const dist = length(uvCoord.sub(center))

      // Normalize rippleTime: starts at 1, we want it to animate from 0 to 1
      // Map rippleTime from 1-2 range to 0-1 range, clamp to prevent overflow
      const normalizedTime = rippleTime.sub(1.0).max(0.0).min(1.0)

      // Use normalized time directly (0 to 1) for animation
      const t = normalizedTime

      // Radius grows from 0 to ~0.4 (fits within 0.5x0.5 geometry, max dist from center is ~0.707)
      const maxRadius = 0.4
      const radius = t.mul(maxRadius)

      // Distance from center minus expanding radius
      const d = dist.sub(radius)

      // Create concentric rings using sin (like GLSL's sin(31.*d))
      // Higher frequency for more visible rings
      const rings = sin(d.mul(25.0))

      // Fade-in from center (smoothstep creates smooth transition)
      const fadeIn = smoothstep(-0.2, -0.1, d)

      // Fade-out at edges (smoothstep creates smooth transition)
      const fadeOut = smoothstep(0.05, -0.05, d)

      // Combine: rings with fade effects
      // Amplify the pattern to make it more visible
      const pattern = rings.mul(fadeIn).mul(fadeOut).mul(2.0)

      // Fade out over time (quadratic fade like GLSL)
      // Start fading after 60% of animation to keep ripples visible
      const timeFade = float(1.0)
        .sub(t.sub(0.6).max(0.0).mul(2.5))
        .max(0.0)
        .pow(1.5)

      return pattern.mul(timeFade)
    })

    const rippleMaterial = new THREE.MeshBasicNodeMaterial()
    rippleMaterial.premultipliedAlpha = true
    rippleMaterial.blending = THREE.MultiplyBlending
    rippleMaterial.colorNode = rippleEffect() // Apply ripple effect
    // Position ripples on the ground surface
    rippleMaterial.positionNode = positionGeometry.add(
      this.ripplePositionBuffer.toAttribute(),
    )
    // Fade out ripples over time
    // Adjust opacity based on rippleTime (similar to original but works with new timing)
    rippleMaterial.opacityNode = Fn(() => {
      // When rippleTime is 1 (just started), we want high opacity
      // When rippleTime increases, fade out
      // Use a calculation that works when rippleTime is 1-2 range
      const timeFactor = rippleTime.sub(1.0).max(0.0).min(1.0)
      const opacity = float(1.0).sub(timeFactor.mul(0.7)).max(0.2)
      return opacity.mul(this.settings.ripplesOpacity)
    })()
    rippleMaterial.side = THREE.DoubleSide
    rippleMaterial.forceSinglePass = true
    rippleMaterial.depthWrite = false
    rippleMaterial.depthTest = true
    rippleMaterial.transparent = true

    // Create ripple geometry (horizontal planes on ground)
    const rippleGeometry = new THREE.PlaneGeometry(0.5, 0.5)
    rippleGeometry.rotateX(-Math.PI / 2) // Rotate to lay flat

    const rippleParticles = new THREE.Mesh(rippleGeometry, rippleMaterial)
    rippleParticles.count = this.instanceCount
    rippleParticles.frustumCulled = false
    this.container.add(rippleParticles)
  }

  update() {
    this.renderer.compute(this.computeParticles)
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }
    const folder = this.debugFolder.addFolder({
      title: "🌧️ Rain",
      expanded: false,
    })

    const settings = {
      dropColor: `#${new THREE.Color(this.settings.dropColor.value).getHexString()}`,
      intensity: this.settings.intensity.value,
      velocity: this.settings.velocity.value,
      ripplesOpacity: this.settings.ripplesOpacity.value,
    }

    folder
      .addBinding(settings, "dropColor", {
        view: "color",
      })
      .on("change", (ev) => {
        this.settings.dropColor.value = new THREE.Color(ev.value)
      })

    folder
      .addBinding(settings, "intensity", {
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.intensity.value = ev.value
      })

    folder
      .addBinding(settings, "velocity", {
        min: 0,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.velocity.value = ev.value
      })

    folder
      .addBinding(settings, "ripplesOpacity", {
        label: "Ripples opacity",
        min: 10,
        max: 40,
        step: 1,
      })
      .on("change", (ev) => {
        this.settings.ripplesOpacity.value = ev.value
      })
  }
}
